import { useForm } from "@inertiajs/react"
import { Plus, X } from "lucide-react"
import { type ChangeEvent, type FormEvent, useRef, useState } from "react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { Recipe, RecipeImportData } from "@/types"

interface IngredientRow {
  id?: number
  quantity: string
  unit: string
  name: string
  position: number
  _destroy?: boolean
}

interface InstructionRow {
  id?: number
  body: string
  position: number
  _destroy?: boolean
}

interface FormData {
  title: string
  description: string
  cover_image: File | null
  ingredients_attributes: IngredientRow[]
  instructions_attributes: InstructionRow[]
}

interface Props {
  recipe?: Recipe
  importData?: RecipeImportData | null
  action: string
  method: "post" | "patch"
}

function buildInitialIngredients(
  recipe?: Recipe,
  importData?: RecipeImportData | null,
): IngredientRow[] {
  if (recipe) {
    return recipe.ingredients.map((i) => ({
      id: i.id,
      quantity: i.quantity?.toString() ?? "",
      unit: i.unit ?? "",
      name: i.name,
      position: i.position,
    }))
  }
  if (importData?.ingredients?.length) {
    return importData.ingredients.map((i, idx) => ({
      quantity: i.quantity?.toString() ?? "",
      unit: i.unit ?? "",
      name: i.name,
      position: idx + 1,
    }))
  }
  return [{ quantity: "", unit: "", name: "", position: 1 }]
}

function buildInitialInstructions(
  recipe?: Recipe,
  importData?: RecipeImportData | null,
): InstructionRow[] {
  if (recipe) {
    return recipe.instructions.map((i) => ({
      id: i.id,
      body: i.body,
      position: i.position,
    }))
  }
  if (importData?.instructions?.length) {
    return importData.instructions.map((i, idx) => ({
      body: i.body,
      position: idx + 1,
    }))
  }
  return [{ body: "", position: 1 }]
}

export default function RecipeForm({
  recipe,
  importData,
  action,
  method,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(
    recipe?.cover_image_url ?? importData?.image_url ?? null,
  )

  const { data, setData, post, patch, processing, errors } = useForm<FormData>({
    title: recipe?.title ?? importData?.title ?? "",
    description: recipe?.description ?? importData?.description ?? "",
    cover_image: null,
    ingredients_attributes: buildInitialIngredients(recipe, importData),
    instructions_attributes: buildInitialInstructions(recipe, importData),
  })

  function handleImageChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setData("cover_image", file)
    if (file) {
      const reader = new FileReader()
      reader.onload = (ev) => setImagePreview(ev.target?.result as string)
      reader.readAsDataURL(file)
    }
  }

  function addIngredient() {
    const maxPosition = Math.max(
      0,
      ...data.ingredients_attributes.map((i) => i.position),
    )
    setData("ingredients_attributes", [
      ...data.ingredients_attributes,
      { quantity: "", unit: "", name: "", position: maxPosition + 1 },
    ])
  }

  function removeIngredient(index: number) {
    const ingredient = data.ingredients_attributes[index]
    if (ingredient.id) {
      const updated = [...data.ingredients_attributes]
      updated[index] = { ...ingredient, _destroy: true }
      setData("ingredients_attributes", updated)
    } else {
      setData(
        "ingredients_attributes",
        data.ingredients_attributes.filter((_, i) => i !== index),
      )
    }
  }

  function updateIngredient(
    index: number,
    field: keyof IngredientRow,
    value: string,
  ) {
    const updated = [...data.ingredients_attributes]
    updated[index] = { ...updated[index], [field]: value }
    setData("ingredients_attributes", updated)
  }

  function addInstruction() {
    const maxPosition = Math.max(
      0,
      ...data.instructions_attributes.map((i) => i.position),
    )
    setData("instructions_attributes", [
      ...data.instructions_attributes,
      { body: "", position: maxPosition + 1 },
    ])
  }

  function removeInstruction(index: number) {
    const instruction = data.instructions_attributes[index]
    if (instruction.id) {
      const updated = [...data.instructions_attributes]
      updated[index] = { ...instruction, _destroy: true }
      setData("instructions_attributes", updated)
    } else {
      setData(
        "instructions_attributes",
        data.instructions_attributes.filter((_, i) => i !== index),
      )
    }
  }

  function updateInstruction(index: number, value: string) {
    const updated = [...data.instructions_attributes]
    updated[index] = { ...updated[index], body: value }
    setData("instructions_attributes", updated)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const submit = method === "patch" ? patch : post
    submit(action, { forceFormData: true })
  }

  const visibleIngredients = data.ingredients_attributes.filter(
    (i) => !i._destroy,
  )
  const visibleInstructions = data.instructions_attributes.filter(
    (i) => !i._destroy,
  )

  return (
    <Card className="mx-auto mt-6 w-full max-w-xl">
    <CardContent>
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          required
          placeholder="Recipe title"
        />
        <InputError messages={errors.title} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          className="border-input bg-white dark:bg-input/30 ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          placeholder="Describe your recipe"
        />
        <InputError messages={errors.description} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="cover_image">Cover Image</Label>
        {imagePreview && (
          <img
            src={imagePreview}
            alt="Preview"
            className="mb-2 aspect-video w-full max-w-sm rounded-lg object-cover"
          />
        )}
        <Input
          id="cover_image"
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageChange}
        />
        <InputError messages={errors.cover_image} />
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <Label>Ingredients</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addIngredient}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add ingredient
          </Button>
        </div>
        <div className="space-y-3">
          {data.ingredients_attributes.map((ingredient, index) => {
            if (ingredient._destroy) return null
            return (
              <div
                key={ingredient.id ?? `new-${index}`}
                className="flex items-start gap-2"
              >
                <Input
                  placeholder="Qty"
                  className="w-20"
                  value={ingredient.quantity}
                  onChange={(e) =>
                    updateIngredient(index, "quantity", e.target.value)
                  }
                />
                <Input
                  placeholder="Unit"
                  className="w-24"
                  value={ingredient.unit}
                  onChange={(e) =>
                    updateIngredient(index, "unit", e.target.value)
                  }
                />
                <Input
                  placeholder="Ingredient name"
                  className="flex-1"
                  value={ingredient.name}
                  onChange={(e) =>
                    updateIngredient(index, "name", e.target.value)
                  }
                  required
                />
                {visibleIngredients.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeIngredient(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <Label>Instructions</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addInstruction}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add step
          </Button>
        </div>
        <div className="space-y-3">
          {data.instructions_attributes.map((instruction, index) => {
            if (instruction._destroy) return null
            const stepNumber =
              visibleInstructions.findIndex((i) => i === instruction) + 1
            return (
              <div
                key={instruction.id ?? `new-${index}`}
                className="flex items-start gap-2"
              >
                <span className="bg-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                  {stepNumber}
                </span>
                <textarea
                  placeholder={`Step ${stepNumber}`}
                  className="border-input bg-white dark:bg-input/30 ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] flex-1 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  value={instruction.body}
                  onChange={(e) => updateInstruction(index, e.target.value)}
                  required
                />
                {visibleInstructions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeInstruction(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <Button type="submit" disabled={processing}>
        {processing ? "Saving..." : "Save Recipe"}
      </Button>
    </form>
    </CardContent>
    </Card>
  )
}
